import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Modal, Pressable, Platform } from 'react-native';
import { Text, Button, Theme } from '../ui';
import { FontFamilyOption, FontSizeOption, ReadingSettings, getReadingSettings, setReadingSettings } from '../../utils/readingSettings';

interface Props {
  visible: boolean;
  onClose: () => void;
  onApply: (settings: ReadingSettings) => void;
}

const ReadingSettingsSheet: React.FC<Props> = ({ visible, onClose, onApply }) => {
  const [settings, setSettings] = useState<ReadingSettings>({ fontFamily: 'outfit', fontSize: 'medium' });

  useEffect(() => {
    if (!visible) return;
    getReadingSettings().then(setSettings);
  }, [visible]);

  const selectFontFamily = (val: FontFamilyOption) => setSettings(prev => ({ ...prev, fontFamily: val }));
  const selectFontSize = (val: FontSizeOption) => setSettings(prev => ({ ...prev, fontSize: val }));

  const handleApply = async () => {
    await setReadingSettings(settings);
    onApply(settings);
    onClose();
  };

  return (
    <Modal transparent visible={visible} animationType="slide" onRequestClose={onClose}>
      <Pressable style={styles.backdrop} onPress={onClose} />
      <View style={styles.sheet}>
        <Text style={styles.title}>Reading settings</Text>

        <View style={styles.group}>
          <Text style={styles.groupTitle}>Font family</Text>
          <View style={styles.row}>
            <Pressable style={[styles.choice, settings.fontFamily === 'serif' && styles.choiceActive]} onPress={() => selectFontFamily('serif')}>
              <Text style={[styles.choiceText, { fontFamily: Platform.select({ ios: 'Times New Roman', android: 'serif', default: 'serif' }) }]}>Serif</Text>
            </Pressable>
            <Pressable style={[styles.choice, settings.fontFamily === 'outfit' && styles.choiceActive]} onPress={() => selectFontFamily('outfit')}>
              <Text style={[styles.choiceText, { fontFamily: 'Outfit-Regular' }]}>Outfit</Text>
            </Pressable>
            <Pressable style={[styles.choice, settings.fontFamily === 'poppins' && styles.choiceActive]} onPress={() => selectFontFamily('poppins')}>
              <Text style={[styles.choiceText, { fontFamily: 'Poppins-Regular' }]}>Poppins</Text>
            </Pressable>
          </View>
        </View>

        <View style={styles.group}>
          <Text style={styles.groupTitle}>Font size</Text>
          <View style={styles.rowWrap}>
            {(['small','medium','large','huge'] as FontSizeOption[]).map((opt) => (
              <Pressable key={opt} style={[styles.choice, settings.fontSize === opt && styles.choiceActive]} onPress={() => selectFontSize(opt)}>
                <Text style={styles.choiceText}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</Text>
              </Pressable>
            ))}
          </View>
        </View>

        <View style={styles.actions}>
          <Button title="Apply" onPress={handleApply} />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.25)'
  },
  sheet: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Theme.colors.surface,
    borderTopLeftRadius: Theme.borderRadius.xl,
    borderTopRightRadius: Theme.borderRadius.xl,
    padding: Theme.spacing.xl,
    gap: Theme.spacing.lg,
  },
  title: {
    fontSize: 18,
    fontFamily: Theme.typography.fontFamily.semiBold,
    color: Theme.colors.textPrimary,
  },
  group: {
    gap: Theme.spacing.sm,
  },
  groupTitle: {
    fontSize: 14,
    color: Theme.colors.textSecondary,
    fontFamily: Theme.typography.fontFamily.medium,
  },
  row: {
    flexDirection: 'row',
    gap: Theme.spacing.md,
  },
  rowWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Theme.spacing.md,
  },
  choice: {
    paddingVertical: Theme.spacing.sm,
    paddingHorizontal: Theme.spacing.lg,
    borderRadius: Theme.borderRadius.md,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.12)'
  },
  choiceActive: {
    backgroundColor: Theme.colors.primaryLight,
    borderColor: Theme.colors.borderActive,
  },
  choiceText: {
    fontSize: 14,
    color: Theme.colors.textPrimary,
  },
  actions: {
    marginTop: Theme.spacing.lg,
  }
});

export default ReadingSettingsSheet;


